import React, { useState, useEffect, memo } from 'react';
import { DollarSign, TrendingUp, Users, Calendar, Download, Filter, Search, Edit, Save, X, Clock, CheckCircle, XCircle, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../lib/api-client';
import { 
  useCommissionTransactions, 
  useCommissionMarketingUsers, 
  useCommissionSettings,
  useApproveCommission,
  useRejectCommission,
  useUpdateCommissionRate,
  useUpdateEmploymentType
} from '../../hooks/useAdminQueries';
import { PaginationWrapper } from '../ui/PaginationWrapper';

interface Transaction {
  _id: string;
  marketingUserId: string;
  marketingUserName: string;
  marketingUserEmail: string;
  couponCode: string;
  buyerName: string;
  buyerEmail: string;
  planName: string;
  originalAmount: number;
  discountAmount: number;
  paidAmount: number;
  commissionAmount: number;
  commissionRate: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  approvedAt?: string;
  approvedBy?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  rejectionReason?: string;
}

interface MarketingUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  marketingData: {
    employmentType?: 'commission' | 'salary';
    totalEarnings?: number;
    pendingEarnings?: number;
    withdrawnEarnings?: number;
    totalSalesCount?: number;
    totalRevenueGenerated?: number;
    isActive?: boolean;
    referralCode?: string;
  };
}

export function AdminCommissions() {
  // React Query hooks - automatic caching and refetching
  const { data: transactions = [], isLoading: transactionsLoading } = useCommissionTransactions();
  const { data: marketingUsers = [], isLoading: usersLoading } = useCommissionMarketingUsers();
  const { data: settingsData, isLoading: settingsLoading, refetch: refetchSettings } = useCommissionSettings();
  
  // Mutations
  const approveMutation = useApproveCommission();
  const rejectMutation = useRejectCommission();
  const updateRateMutation = useUpdateCommissionRate();
  const updateEmploymentTypeMutation = useUpdateEmploymentType();

  // Local state
  const [activeTab, setActiveTab] = useState<'pending' | 'approved' | 'rejected' | 'users'>('pending');
  const [commissionRate, setCommissionRate] = useState(settingsData?.commissionRate || 50);
  const [isEditingRate, setIsEditingRate] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [selectedUser, setSelectedUser] = useState<MarketingUser | null>(null);
  const [newEmploymentType, setNewEmploymentType] = useState<'commission' | 'salary'>('commission');

  // Update local commission rate when settings data changes
  useEffect(() => {
    if (settingsData?.commissionRate) {
      setCommissionRate(settingsData.commissionRate);
    }
  }, [settingsData]);

  const loading = transactionsLoading || usersLoading || settingsLoading;
  const updating = approveMutation.isPending || rejectMutation.isPending || updateRateMutation.isPending;

  const handleApprove = async (transactionId: string) => {
    await approveMutation.mutateAsync(transactionId);
    setSelectedTransaction(null);
  };

  const handleReject = async (transactionId: string) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    await rejectMutation.mutateAsync({ transactionId, reason: rejectionReason });
    setSelectedTransaction(null);
    setRejectionReason('');
  };

  const handleSaveCommissionRate = async () => {
    if (commissionRate < 0 || commissionRate > 100) {
      toast.error('Commission rate must be between 0 and 100');
      return;
    }

    await updateRateMutation.mutateAsync(commissionRate);
    setIsEditingRate(false);
  };

  const handleChangeEmploymentType = async () => {
    if (!selectedUser) return;

    await updateEmploymentTypeMutation.mutateAsync({ 
      userId: selectedUser._id, 
      employmentType: newEmploymentType 
    });
    setSelectedUser(null);
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingTransactions = transactions.filter(t => t.status === 'pending');
  const approvedTransactions = transactions.filter(t => t.status === 'approved');
  const rejectedTransactions = transactions.filter(t => t.status === 'rejected');

  const totalPending = pendingTransactions.reduce((sum, t) => sum + t.commissionAmount, 0);
  const totalApproved = approvedTransactions.reduce((sum, t) => sum + t.commissionAmount, 0);
  const totalRejected = rejectedTransactions.reduce((sum, t) => sum + t.commissionAmount, 0);

  const TransactionRow = ({ transaction }: { transaction: Transaction }) => (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="py-4 px-4 text-sm text-gray-600">
        {formatDate(transaction.createdAt)}
      </td>
      <td className="py-4 px-4">
        <div>
          <div className="text-sm font-medium text-gray-900">{transaction.marketingUserName}</div>
          <div className="text-xs text-gray-500">{transaction.marketingUserEmail}</div>
        </div>
      </td>
      <td className="py-4 px-4">
        <div>
          <div className="text-sm font-medium text-gray-900">{transaction.buyerName}</div>
          <div className="text-xs text-gray-500">{transaction.buyerEmail}</div>
        </div>
      </td>
      <td className="py-4 px-4">
        <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-mono font-medium">
          {transaction.couponCode}
        </span>
      </td>
      <td className="py-4 px-4 text-sm text-gray-900">{transaction.planName}</td>
      <td className="py-4 px-4 text-right">
        <div className="text-sm text-gray-600">
          {formatCurrency(transaction.originalAmount)}
        </div>
        <div className="text-xs text-red-600">
          -{formatCurrency(transaction.discountAmount)}
        </div>
      </td>
      <td className="py-4 px-4 text-right text-sm font-medium text-gray-900">
        {formatCurrency(transaction.paidAmount)}
      </td>
      <td className="py-4 px-4 text-right">
        <div className="text-sm font-bold text-green-600">
          {formatCurrency(transaction.commissionAmount)}
        </div>
        <div className="text-xs text-gray-500">
          ({transaction.commissionRate}%)
        </div>
      </td>
      <td className="py-4 px-4 text-center">
        {transaction.status === 'pending' && (
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => handleApprove(transaction._id)}
              disabled={updating}
              className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50"
              title="Approve"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
            <button
              onClick={() => setSelectedTransaction(transaction)}
              disabled={updating}
              className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors disabled:opacity-50"
              title="Reject"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}
        {transaction.status === 'approved' && (
          <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Approved
          </div>
        )}
        {transaction.status === 'rejected' && (
          <div className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Rejected
          </div>
        )}
      </td>
    </tr>
  );

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading commission data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-gray-900 mb-2">Commission Management</h2>
        <p className="text-gray-600">Manage marketing user commissions and approve transactions</p>
      </div>

      {/* Commission Rate Setting */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white mb-2">Global Commission Rate</h3>
            <p className="text-sm opacity-90">Set the commission percentage for all marketing users</p>
          </div>
          <div className="flex items-center gap-4">
            {isEditingRate ? (
              <>
                <input
                  type="number"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(Number(e.target.value))}
                  min="0"
                  max="100"
                  className="w-24 px-3 py-2 rounded-lg text-gray-900 text-center font-bold text-xl"
                />
                <span className="text-2xl font-bold">%</span>
                <button
                  onClick={handleSaveCommissionRate}
                  disabled={updating}
                  className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors disabled:opacity-50"
                >
                  {updating ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setIsEditingRate(false);
                    refetchSettings(); // Reset to original value
                  }}
                  className="bg-white/20 text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <div className="text-5xl font-bold">{commissionRate}%</div>
                <button
                  onClick={() => setIsEditingRate(true)}
                  className="bg-white text-purple-600 px-4 py-2 rounded-lg font-medium hover:bg-purple-50 transition-colors"
                >
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <Clock className="w-8 h-8 text-orange-500" />
            <span className="text-sm text-gray-600">Pending</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalPending)}</div>
          <p className="text-sm text-gray-600">{pendingTransactions.length} transactions</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <span className="text-sm text-gray-600">Approved</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalApproved)}</div>
          <p className="text-sm text-gray-600">{approvedTransactions.length} transactions</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
            <span className="text-sm text-gray-600">Rejected</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalRejected)}</div>
          <p className="text-sm text-gray-600">{rejectedTransactions.length} transactions</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="w-8 h-8 text-blue-500" />
            <span className="text-sm text-gray-600">Total</span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalPending + totalApproved)}</div>
          <p className="text-sm text-gray-600">{transactions.length} transactions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <div className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'pending'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Pending ({pendingTransactions.length})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'approved'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Approved ({approvedTransactions.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'rejected'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Rejected ({rejectedTransactions.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Marketing Users ({marketingUsers.length})
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Pending Tab */}
          {activeTab === 'pending' && (
            <div>
              {pendingTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No pending transactions</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Marketing User</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Buyer</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Coupon</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Plan</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-700">Original</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-700">Paid</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-700">Commission</th>
                        <th className="text-center py-3 px-4 text-sm text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingTransactions.map(transaction => (
                        <TransactionRow key={transaction._id} transaction={transaction} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Approved Tab */}
          {activeTab === 'approved' && (
            <div>
              {approvedTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No approved transactions</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Marketing User</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Buyer</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Coupon</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Plan</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-700">Original</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-700">Paid</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-700">Commission</th>
                        <th className="text-center py-3 px-4 text-sm text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedTransactions.map(transaction => (
                        <TransactionRow key={transaction._id} transaction={transaction} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Rejected Tab */}
          {activeTab === 'rejected' && (
            <div>
              {rejectedTransactions.length === 0 ? (
                <div className="text-center py-12">
                  <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No rejected transactions</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Marketing User</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Buyer</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Coupon</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-700">Plan</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-700">Commission</th>
                        <th className="text-center py-3 px-4 text-sm text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rejectedTransactions.map(transaction => (
                        <TransactionRow key={transaction._id} transaction={transaction} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Marketing Users Tab */}
          {activeTab === 'users' && (
            <div>
              {marketingUsers.length === 0 ? (
                <div className="text-center py-12">
                  <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No marketing users</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {marketingUsers.map(user => {
                    const employmentType = user.marketingData?.employmentType || 'commission';
                    const isCommission = employmentType === 'commission';
                    
                    return (
                      <div key={user._id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-3">
                              <h4 className="text-gray-900 font-medium">
                                {user.firstName} {user.lastName}
                              </h4>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                isCommission 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {isCommission ? '💵 Commission' : '💰 Salary'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            {user.phone && (
                              <p className="text-sm text-gray-500">{user.phone}</p>
                            )}
                          </div>
                        </div>
                        
                        {isCommission ? (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <div className="text-sm text-gray-600">Total Earnings</div>
                              <div className="font-medium text-gray-900">
                                {formatCurrency(user.marketingData?.totalEarnings || 0)}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600">Pending</div>
                              <div className="font-medium text-orange-600">
                                {formatCurrency(user.marketingData?.pendingEarnings || 0)}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600">Withdrawn</div>
                              <div className="font-medium text-gray-900">
                                {formatCurrency(user.marketingData?.withdrawnEarnings || 0)}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600">Total Sales</div>
                              <div className="font-medium text-gray-900">
                                {user.marketingData?.totalSalesCount || 0} plans
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            <div>
                              <div className="text-sm text-gray-600">Total Plans Sold</div>
                              <div className="font-medium text-gray-900">
                                {user.marketingData?.totalSalesCount || 0} plans
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600">Revenue Generated</div>
                              <div className="font-medium text-blue-600">
                                {formatCurrency(user.marketingData?.totalRevenueGenerated || 0)}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm text-gray-600">Commission</div>
                              <div className="font-medium text-gray-500">
                                N/A (Salary-based)
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                          <div className="text-sm text-gray-600">
                            {user.marketingData?.referralCode && (
                              <span className="font-mono bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                {user.marketingData.referralCode}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setNewEmploymentType(employmentType === 'commission' ? 'salary' : 'commission');
                            }}
                            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Change Employment Type
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-gray-900 mb-4">Reject Transaction</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for rejecting this commission:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent mb-4"
              rows={4}
            />
            <div className="flex gap-3">
              <button
                onClick={() => handleReject(selectedTransaction._id)}
                disabled={updating}
                className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {updating ? 'Rejecting...' : 'Reject'}
              </button>
              <button
                onClick={() => {
                  setSelectedTransaction(null);
                  setRejectionReason('');
                }}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employment Type Change Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-gray-900 mb-4">Change Employment Type</h3>
            <p className="text-sm text-gray-600 mb-4">
              Update employment type for <span className="font-medium">{selectedUser.firstName} {selectedUser.lastName}</span>
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Employment Type
              </label>
              <div className="space-y-3">
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${newEmploymentType === 'commission' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="employmentType"
                    value="commission"
                    checked={newEmploymentType === 'commission'}
                    onChange={(e) => setNewEmploymentType(e.target.value as 'commission' | 'salary')}
                    className="w-4 h-4 text-green-600"
                  />
                  <div className="ml-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">💵 Commission-based</span>
                    </div>
                    <p className="text-sm text-gray-600">Earn {commissionRate}% commission on each sale</p>
                  </div>
                </label>
                
                <label className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${newEmploymentType === 'salary' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                  <input
                    type="radio"
                    name="employmentType"
                    value="salary"
                    checked={newEmploymentType === 'salary'}
                    onChange={(e) => setNewEmploymentType(e.target.value as 'commission' | 'salary')}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div className="ml-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">💰 Salary-based</span>
                    </div>
                    <p className="text-sm text-gray-600">Fixed salary with performance tracking</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Changing to salary-based will stop commission earnings. Sales will still be tracked for performance reporting.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleChangeEmploymentType}
                disabled={updateEmploymentTypeMutation.isPending}
                className="flex-1 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50"
              >
                {updateEmploymentTypeMutation.isPending ? 'Updating...' : 'Update Employment Type'}
              </button>
              <button
                onClick={() => setSelectedUser(null)}
                disabled={updateEmploymentTypeMutation.isPending}
                className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(AdminCommissions);